import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import AdminPagination from '../../components/AdminPagination';
import CancelOrderModal from '../../../components/CancelOrderModal';
import './DeliveryView.css';

const MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const DeliveryView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverLocations, setDriverLocations] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [mobileShowList, setMobileShowList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelOrderNumber, setCancelOrderNumber] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);
  const driverRouteRendererRef = useRef(null); // Separate renderer for driver route
  const refreshIntervalRef = useRef(null);
  const driverMarkersMapRef = useRef(new Map()); // Map driver ID to marker
  const mapInitializedRef = useRef(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (user) {
      // Initial fetch
      fetchOrders();
      fetchDriverLocations();
      
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      
      // Refresh orders and driver locations every 30 seconds for the map view
      refreshIntervalRef.current = setInterval(() => {
        fetchOrders();
        fetchDriverLocations();
      }, 30000); // 30 seconds for map view refresh
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Initialize map only once when Google Maps loads or when selectedOrder changes
  useEffect(() => {
    if (googleMapsLoaded && window.google && window.google.maps) {
      const timer = setTimeout(() => {
        if (selectedOrder) {
          if (!mapInitializedRef.current || !mapInstanceRef.current) {
            initializeMap();
            mapInitializedRef.current = true;
          } else {
            // Update existing map instead of recreating
            updateMapForSelectedOrder();
          }
        } else {
          if (!mapInitializedRef.current || !mapInstanceRef.current) {
            initializeMapWithAllDrivers();
            mapInitializedRef.current = true;
          } else {
            // Update existing map instead of recreating
            updateMapWithAllDrivers();
          }
        }
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedOrder, googleMapsLoaded]);

  // Update driver locations without reinitializing the map
  useEffect(() => {
    if (mapInitializedRef.current && mapInstanceRef.current && driverLocations.length > 0) {
      if (selectedOrder) {
        updateMapForSelectedOrder();
      } else {
        updateMapWithAllDrivers();
      }
    }
  }, [driverLocations]);

  const fetchOrders = async () => {
    try {
      if ((user?.role === 'main_admin' || user?.role === 'admin')) {
        // Main admin sees all orders
        const response = await axios.get(`${API_URL}/admin/orders`);
        setOrders(response.data);
      } else {
        // Drivers see their assigned and available orders
        const assignedResponse = await axios.get(`${API_URL}/delivery-driver/orders`);
        const availableResponse = await axios.get(`${API_URL}/delivery-driver/orders/available`);
        // Combine both lists, prioritizing assigned orders
        const allOrders = [...assignedResponse.data, ...availableResponse.data];
        // Remove duplicates based on order ID
        const uniqueOrders = allOrders.filter((order, index, self) =>
          index === self.findIndex((o) => o._id === order._id)
        );
        setOrders(uniqueOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverLocations = async () => {
    if (!user) return;
    
    try {
      // Use different endpoint based on user role
      const endpoint = (user.role === 'main_admin' || user.role === 'admin') 
        ? `${API_URL}/admin/delivery/driver-locations`
        : `${API_URL}/delivery-driver/locations`;
      
      const response = await axios.get(endpoint);
      setDriverLocations(response.data || []);
    } catch (error) {
      // Only log error if it's not a 403 (permission denied) for non-admin users
      // 403 is expected for drivers trying to access admin endpoints
      if ((user.role === 'main_admin' || user.role === 'admin') || error.response?.status !== 403) {
        console.error('Error fetching driver locations:', error);
      }
      // Set empty array on error to prevent issues
      setDriverLocations([]);
    }
  };

  const handleOrderSelect = async (order) => {
    if (isMobile) setMobileShowList(false);
    try {
      // Use different endpoint based on user role
      if ((user?.role === 'main_admin' || user?.role === 'admin')) {
        // Main admin can access admin endpoint
        const response = await axios.get(`${API_URL}/admin/orders/${order._id}`);
        setSelectedOrder(response.data);
      } else {
        // Drivers use driver-specific endpoint (only for their assigned orders)
        try {
          const response = await axios.get(`${API_URL}/delivery-driver/orders/${order._id}`);
          setSelectedOrder(response.data);
        } catch (error) {
          // If driver endpoint fails, use the order data we already have
          // This handles cases where order is available but not yet assigned
          setSelectedOrder(order);
        }
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      // Fallback to the order from list if detail fetch fails
      setSelectedOrder(order);
    }
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    driverMarkersMapRef.current.clear();
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }
    if (driverRouteRendererRef.current) {
      driverRouteRendererRef.current.setMap(null);
    }
  };

  // Update map for selected order without recreating everything
  const updateMapForSelectedOrder = () => {
    if (!mapInstanceRef.current || !selectedOrder || !window.google || !window.google.maps) {
      return;
    }

    const restaurant = selectedOrder.restaurant;
    if (!restaurant?.address?.coordinates?.lat || !restaurant?.address?.coordinates?.lng) {
      return;
    }

    // Update driver marker position if it exists
    if (selectedOrder.deliveryDriver?.currentLocation?.coordinates) {
      const driverLocation = selectedOrder.deliveryDriver.currentLocation.coordinates;
      const driverId = selectedOrder.deliveryDriver._id?.toString();
      
      if (driverId && driverMarkersMapRef.current.has(driverId)) {
        const driverMarker = driverMarkersMapRef.current.get(driverId);
        const newPosition = new window.google.maps.LatLng(driverLocation.lat, driverLocation.lng);
        driverMarker.setPosition(newPosition);
      }
    }
  };

  // Update map with all drivers without recreating everything
  const updateMapWithAllDrivers = () => {
    if (!mapInstanceRef.current || !window.google || !window.google.maps) {
      return;
    }

    // Update existing driver markers or create new ones
    driverLocations.forEach((driver) => {
      if (driver.currentLocation?.coordinates?.lat && driver.currentLocation?.coordinates?.lng) {
        const driverId = driver._id?.toString();
        const newPosition = new window.google.maps.LatLng(
          driver.currentLocation.coordinates.lat,
          driver.currentLocation.coordinates.lng
        );

        if (driverMarkersMapRef.current.has(driverId)) {
          // Update existing marker position smoothly
          const marker = driverMarkersMapRef.current.get(driverId);
          marker.setPosition(newPosition);
        } else {
          // Create new marker if it doesn't exist
          const driverMarker = new window.google.maps.Marker({
            position: newPosition,
            map: mapInstanceRef.current,
            title: `${driver.firstName} ${driver.lastName}`,
            icon: {
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new window.google.maps.Size(28, 28)
            },
            label: {
              text: '🚗',
              fontSize: '18px'
            },
            animation: window.google.maps.Animation.DROP
          });
          
          driverMarkersMapRef.current.set(driverId, driverMarker);
          markersRef.current.push(driverMarker);

          const driverInfoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <strong>Driver: ${driver.firstName} ${driver.lastName}</strong><br/>
                ${(driver.driverGroups && driver.driverGroups.length) ? `Groups: ${driver.driverGroups.map(g => g.name).join(', ')}` : (driver.driverGroup ? `Group: ${driver.driverGroup.name}` : 'No group assigned')}<br/>
                Last updated: ${driver.currentLocation.lastUpdated ? new Date(driver.currentLocation.lastUpdated).toLocaleString() : 'N/A'}
              </div>
            `
          });
          driverMarker.addListener('click', () => {
            driverInfoWindow.open(mapInstanceRef.current, driverMarker);
          });
        }
      }
    });

    // Remove markers for drivers that are no longer in the list
    const currentDriverIds = new Set(driverLocations.map(d => d._id?.toString()).filter(Boolean));
    driverMarkersMapRef.current.forEach((marker, driverId) => {
      if (!currentDriverIds.has(driverId)) {
        marker.setMap(null);
        driverMarkersMapRef.current.delete(driverId);
        const index = markersRef.current.indexOf(marker);
        if (index > -1) {
          markersRef.current.splice(index, 1);
        }
      }
    });
  };

  const initializeMap = () => {
    if (!mapRef.current || !selectedOrder || !window.google || !window.google.maps) {
      return;
    }

    clearMarkers();

    const restaurant = selectedOrder.restaurant;
    if (!restaurant) {
      console.warn('Restaurant data missing for order');
      return;
    }

    // Handle both Restaurant and Store types
    const restaurantAddress = restaurant.address || restaurant.address;
    if (!restaurantAddress?.coordinates?.lat || !restaurantAddress?.coordinates?.lng) {
      console.warn('Restaurant address coordinates missing');
      return;
    }

    const restaurantLat = restaurantAddress.coordinates.lat;
    const restaurantLng = restaurantAddress.coordinates.lng;
    const deliveryAddress = selectedOrder.deliveryAddress;

    // Calculate center point
    let center = { lat: restaurantLat, lng: restaurantLng };
    const bounds = new window.google.maps.LatLngBounds();

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 13,
        center: center,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      });
    }

    // Add restaurant marker
    const restaurantMarker = new window.google.maps.Marker({
      position: { lat: restaurantLat, lng: restaurantLng },
      map: mapInstanceRef.current,
      title: restaurant.name || selectedOrder.restaurantName,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new window.google.maps.Size(32, 32)
      },
      label: {
        text: 'R',
        color: 'white',
        fontWeight: 'bold'
      }
    });
    markersRef.current.push(restaurantMarker);
    bounds.extend({ lat: restaurantLat, lng: restaurantLng });

    // Add restaurant info window
    const restaurantInfoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 8px;">
          <strong>${restaurant.name || selectedOrder.restaurantName}</strong><br/>
          ${restaurantAddress?.street || ''}<br/>
          ${restaurantAddress?.city || ''}
        </div>
      `
    });
    restaurantMarker.addListener('click', () => {
      restaurantInfoWindow.open(mapInstanceRef.current, restaurantMarker);
    });

    // Add delivery address marker if available
    if (deliveryAddress?.coordinates?.lat && deliveryAddress?.coordinates?.lng) {
      const deliveryMarker = new window.google.maps.Marker({
        position: {
          lat: deliveryAddress.coordinates.lat,
          lng: deliveryAddress.coordinates.lng
        },
        map: mapInstanceRef.current,
        title: 'Delivery Address',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
          scaledSize: new window.google.maps.Size(32, 32)
        },
        label: {
          text: 'D',
          color: 'white',
          fontWeight: 'bold'
        }
      });
      markersRef.current.push(deliveryMarker);
      bounds.extend({
        lat: deliveryAddress.coordinates.lat,
        lng: deliveryAddress.coordinates.lng
      });

      // Add delivery address info window
      const deliveryInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <strong>Delivery Address</strong><br/>
            ${deliveryAddress.street || ''}<br/>
            ${deliveryAddress.city || ''} ${deliveryAddress.zipCode || ''}${deliveryAddress.addressDetails ? `<br/>${deliveryAddress.addressDetails}` : ''}
          </div>
        `
      });
      deliveryMarker.addListener('click', () => {
        deliveryInfoWindow.open(mapInstanceRef.current, deliveryMarker);
      });

      // Draw route from restaurant to delivery address
      if (window.google.maps.DirectionsService && window.google.maps.DirectionsRenderer) {
        const directionsService = new window.google.maps.DirectionsService();
        if (!directionsRendererRef.current) {
          directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
            map: mapInstanceRef.current,
            suppressMarkers: true,
            preserveViewport: true // Don't change viewport when updating route
          });
        } else {
          directionsRendererRef.current.setMap(mapInstanceRef.current);
        }

        // Always update route to ensure it's visible
        directionsService.route({
          origin: { lat: restaurantLat, lng: restaurantLng },
          destination: {
            lat: deliveryAddress.coordinates.lat,
            lng: deliveryAddress.coordinates.lng
          },
          travelMode: window.google.maps.TravelMode.DRIVING
        }, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
          }
        });
      }
    }

    // Add driver marker for the selected order if available
    if (selectedOrder.deliveryDriver && selectedOrder.deliveryDriver.currentLocation?.coordinates) {
      const driverLocation = selectedOrder.deliveryDriver.currentLocation.coordinates;
      if (driverLocation.lat && driverLocation.lng) {
        const driverId = selectedOrder.deliveryDriver._id?.toString();
        const driverPosition = { lat: driverLocation.lat, lng: driverLocation.lng };
        
        let driverMarker;
        if (driverId && driverMarkersMapRef.current.has(driverId)) {
          // Update existing marker
          driverMarker = driverMarkersMapRef.current.get(driverId);
          driverMarker.setPosition(driverPosition);
        } else {
          // Create new marker
          driverMarker = new window.google.maps.Marker({
            position: driverPosition,
            map: mapInstanceRef.current,
            title: 'Driver',
            icon: {
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new window.google.maps.Size(32, 32)
            },
            label: {
              text: '🚗',
              fontSize: '20px'
            },
            animation: window.google.maps.Animation.DROP
          });
          markersRef.current.push(driverMarker);
          if (driverId) {
            driverMarkersMapRef.current.set(driverId, driverMarker);
          }

          const driverInfoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <strong>Driver</strong><br/>
                ${selectedOrder.deliveryDriver.firstName || ''} ${selectedOrder.deliveryDriver.lastName || ''}
              </div>
            `
          });
          driverMarker.addListener('click', () => {
            driverInfoWindow.open(mapInstanceRef.current, driverMarker);
          });
        }
        
        bounds.extend(driverPosition);

        // Draw route from restaurant to driver
        if (window.google.maps.DirectionsService && window.google.maps.DirectionsRenderer) {
          const driverDirectionsService = new window.google.maps.DirectionsService();
          
          // Create or reuse driver route renderer
          if (!driverRouteRendererRef.current) {
            driverRouteRendererRef.current = new window.google.maps.DirectionsRenderer({
              map: mapInstanceRef.current,
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: '#4285F4',
                strokeWeight: 4,
                strokeOpacity: 0.7
              }
            });
          } else {
            driverRouteRendererRef.current.setMap(mapInstanceRef.current);
          }

          driverDirectionsService.route({
            origin: { lat: restaurantLat, lng: restaurantLng },
            destination: driverPosition,
            travelMode: window.google.maps.TravelMode.DRIVING
          }, (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK && driverRouteRendererRef.current) {
              driverRouteRendererRef.current.setDirections(result);
            }
          });
        }
      }
    }

    // Add all other driver locations
    driverLocations.forEach((driver) => {
      // Skip if this is the driver for the selected order (already added above)
      if (selectedOrder.deliveryDriver && driver._id.toString() === selectedOrder.deliveryDriver._id?.toString()) {
        return;
      }

      if (driver.currentLocation?.coordinates?.lat && driver.currentLocation?.coordinates?.lng) {
        const driverId = driver._id?.toString();
        const driverPosition = {
          lat: driver.currentLocation.coordinates.lat,
          lng: driver.currentLocation.coordinates.lng
        };
        
        let driverMarker;
        if (driverId && driverMarkersMapRef.current.has(driverId)) {
          // Update existing marker
          driverMarker = driverMarkersMapRef.current.get(driverId);
          driverMarker.setPosition(driverPosition);
        } else {
          // Create new marker
          driverMarker = new window.google.maps.Marker({
            position: driverPosition,
            map: mapInstanceRef.current,
            title: `${driver.firstName} ${driver.lastName}`,
            icon: {
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new window.google.maps.Size(28, 28)
            },
            label: {
              text: '🚗',
              fontSize: '18px'
            },
            animation: window.google.maps.Animation.DROP
          });
          markersRef.current.push(driverMarker);
          if (driverId) {
            driverMarkersMapRef.current.set(driverId, driverMarker);
          }

          const driverInfoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <strong>Driver: ${driver.firstName} ${driver.lastName}</strong><br/>
                ${(driver.driverGroups && driver.driverGroups.length) ? `Groups: ${driver.driverGroups.map(g => g.name).join(', ')}` : (driver.driverGroup ? `Group: ${driver.driverGroup.name}` : 'No group assigned')}<br/>
                Last updated: ${driver.currentLocation.lastUpdated ? new Date(driver.currentLocation.lastUpdated).toLocaleString() : 'N/A'}
              </div>
            `
          });
          driverMarker.addListener('click', () => {
            driverInfoWindow.open(mapInstanceRef.current, driverMarker);
          });
        }
        
        bounds.extend(driverPosition);
      }
    });

    // Fit bounds to show all markers
    if (bounds.getNorthEast().lat() !== bounds.getSouthWest().lat() ||
        bounds.getNorthEast().lng() !== bounds.getSouthWest().lng()) {
      mapInstanceRef.current.fitBounds(bounds);
      mapInstanceRef.current.setZoom(Math.min(mapInstanceRef.current.getZoom(), 15));
    } else {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(13);
    }
  };

  const initializeMapWithAllDrivers = () => {
    if (!mapRef.current || !googleMapsLoaded || !window.google || !window.google.maps) {
      return;
    }

    clearMarkers();

    if (driverLocations.length === 0) {
      // Default center if no drivers
      const defaultCenter = { lat: 60.1699, lng: 24.9384 }; // Helsinki
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          zoom: 10,
          center: defaultCenter,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true
        });
      } else {
        mapInstanceRef.current.setCenter(defaultCenter);
        mapInstanceRef.current.setZoom(10);
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 10,
        center: { lat: 60.1699, lng: 24.9384 },
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      });
    }

    // Add all driver locations
    driverLocations.forEach((driver) => {
      if (driver.currentLocation?.coordinates?.lat && driver.currentLocation?.coordinates?.lng) {
        const driverMarker = new window.google.maps.Marker({
          position: {
            lat: driver.currentLocation.coordinates.lat,
            lng: driver.currentLocation.coordinates.lng
          },
          map: mapInstanceRef.current,
          title: `${driver.firstName} ${driver.lastName}`,
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          },
          label: {
            text: '🚗',
            fontSize: '20px'
          }
        });
        markersRef.current.push(driverMarker);
        bounds.extend({
          lat: driver.currentLocation.coordinates.lat,
          lng: driver.currentLocation.coordinates.lng
        });

        const driverInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px;">
              <strong>Driver: ${driver.firstName} ${driver.lastName}</strong><br/>
              ${(driver.driverGroups && driver.driverGroups.length) ? `Groups: ${driver.driverGroups.map(g => g.name).join(', ')}` : (driver.driverGroup ? `Group: ${driver.driverGroup.name}` : 'No group assigned')}<br/>
              Last updated: ${driver.currentLocation.lastUpdated ? new Date(driver.currentLocation.lastUpdated).toLocaleString() : 'N/A'}
            </div>
          `
        });
        driverMarker.addListener('click', () => {
          driverInfoWindow.open(mapInstanceRef.current, driverMarker);
        });
      }
    });

    // Fit bounds to show all drivers
    if (driverLocations.length > 0 && bounds.getNorthEast().lat() !== bounds.getSouthWest().lat()) {
      mapInstanceRef.current.fitBounds(bounds);
      mapInstanceRef.current.setZoom(Math.min(mapInstanceRef.current.getZoom(), 12));
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#f39c12', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      delivered: { bg: '#27ae60', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (newStatus === 'cancelled') {
      const order = orders.find(o => o._id === orderId);
      setCancelOrderId(orderId);
      setCancelOrderNumber(order?.orderNumber);
      setShowCancelModal(true);
      return;
    }
    try {
      let endpoint;
      if ((user?.role === 'main_admin' || user?.role === 'admin')) {
        endpoint = `${API_URL}/admin/orders/${orderId}/status`;
      } else {
        endpoint = `${API_URL}/delivery-driver/orders/${orderId}/status`;
      }
      const response = await axios.patch(endpoint, { status: newStatus });
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId ? response.data : order
        )
      );
      if (selectedOrder?._id === orderId) {
        setSelectedOrder(response.data);
      }
      fetchOrders();
    } catch (error) {
      console.error('Error changing order status:', error);
      showError(error.response?.data?.message || 'Error changing order status');
    }
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!cancelOrderId) return;
    try {
      let endpoint;
      if ((user?.role === 'main_admin' || user?.role === 'admin')) {
        endpoint = `${API_URL}/admin/orders/${cancelOrderId}/status`;
      } else {
        endpoint = `${API_URL}/delivery-driver/orders/${cancelOrderId}/status`;
      }
      const response = await axios.patch(endpoint, {
        status: 'cancelled',
        cancellationMessage
      });
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === cancelOrderId ? response.data : order
        )
      );
      if (selectedOrder?._id === cancelOrderId) {
        setSelectedOrder(response.data);
      }
      setShowCancelModal(false);
      setCancelOrderId(null);
      setCancelOrderNumber(null);
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(error.response?.data?.message || 'Error changing order status');
    }
  };

  const getAvailableStatuses = (order) => {
    if ((user?.role === 'main_admin' || user?.role === 'admin')) {
      const allStatuses = ['pending', 'confirmed', 'preparing', 'readyForPickup', 'outForDelivery', 'delivered', 'cancelled'];
      return allStatuses.filter(status => status !== order.status);
    } else if (user?.role === 'delivery_driver') {
      const driverId = typeof order.deliveryDriver === 'object' ? order.deliveryDriver?._id : order.deliveryDriver;
      const currentUserId = user._id || user.id;
      if (driverId && driverId.toString() === currentUserId.toString()) {
        const currentStatus = order.status;
        if (currentStatus === 'readyForPickup' || currentStatus === 'preparing' || currentStatus === 'confirmed') {
          return ['outForDelivery', 'cancelled'];
        }
        if (currentStatus === 'outForDelivery') {
          return ['delivered', 'cancelled'];
        }
      }
      return [];
    }
    return [];
  };

  const isPendingDriverAcceptance = (order) => {
    if (user?.role !== 'delivery_driver') return false;
    if (order.status === 'delivered' || order.status === 'cancelled') return false;
    const driverId = typeof order.deliveryDriver === 'object' ? order.deliveryDriver?._id : order.deliveryDriver;
    const currentUserId = user._id || user.id;
    return driverId && driverId.toString() === currentUserId.toString() && !order.deliveryDriverAcceptedAt;
  };

  const isAvailableForClaim = (order) => {
    if (user?.role !== 'delivery_driver') return false;
    return !order.deliveryDriver && order.status === 'pending' && order.deliveryMethod === 'delivery';
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/accept`);
      setOrders(prev => prev.map(o => o._id === orderId ? response.data : o));
      if (selectedOrder?._id === orderId) setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      console.error('Error accepting order:', error);
      showError(error.response?.data?.message || 'Failed to accept order');
    }
  };

  const handleAcceptDelivery = async (orderId) => {
    try {
      const response = await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/accept-delivery`);
      setOrders(prev => prev.map(o => o._id === orderId ? response.data : o));
      if (selectedOrder?._id === orderId) setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      console.error('Error accepting delivery:', error);
      showError(error.response?.data?.message || 'Failed to accept delivery');
    }
  };

  const handleDeclineDelivery = async (orderId) => {
    try {
      await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/decline-delivery`);
      setOrders(prev => prev.filter(o => o._id !== orderId));
      if (selectedOrder?._id === orderId) setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error declining delivery:', error);
      showError(error.response?.data?.message || 'Failed to decline delivery');
    }
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(start, start + itemsPerPage);
  }, [filteredOrders, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrders.length, itemsPerPage]);

  if (loading) {
    return (
      <div className="delivery-view-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  const statusOptions = [
    { value: 'all', label: t('admin.all') },
    { value: 'pending', label: t('admin.pending') },
    { value: 'confirmed', label: t('admin.confirmed') },
    { value: 'preparing', label: t('admin.preparing') },
    { value: 'readyForPickup', label: t('admin.readyForPickup') || 'Ready for pickup' },
    { value: 'outForDelivery', label: t('admin.outForDelivery') },
    { value: 'delivered', label: t('admin.delivered') },
    { value: 'cancelled', label: t('admin.cancelled') }
  ];

  return (
    <div className="delivery-view-container">
      <div className="delivery-view-header">
        <h2>{t('admin.deliveryView')}</h2>
        <div className="status-filter-buttons">
          {statusOptions.map(status => (
            <button
              key={status.value}
              className={`status-filter-btn ${statusFilter === status.value ? 'active' : ''}`}
              onClick={() => setStatusFilter(status.value)}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      <div className="delivery-view-content">
        {(!isMobile || mobileShowList || !selectedOrder) && (
        <div className="orders-list-panel">
          <h3>{t('admin.orders')} ({filteredOrders.length})</h3>
          <div className="orders-list">
            {filteredOrders.length === 0 ? (
              <div className="no-orders">{t('admin.noOrdersAvailable')}</div>
            ) : (
              paginatedOrders.map(order => (
                <div
                  key={order._id}
                  className={`order-item ${selectedOrder?._id === order._id ? 'selected' : ''}`}
                  onClick={() => handleOrderSelect(order)}
                >
                  <div className="order-item-header">
                    <div className="order-number">#{order.orderNumber || order._id.slice(-6)}</div>
                    {getStatusBadge(order.status)}
                    {(user?.role === 'main_admin' || user?.role === 'admin') && order.deliveryDriver && !order.deliveryDriverAcceptedAt && order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <span className="order-badge-pending-acceptance" title={t('admin.acceptDeliveryPrompt') || 'Driver has not accepted yet'}>
                        {t('admin.pendingAcceptance') || 'Pending acceptance'}
                      </span>
                    )}
                  </div>
                  {order.status === 'cancelled' && order.cancellationMessage && (
                    <div className="order-cancellation-message" onClick={e => e.stopPropagation()}>
                      <span className="order-cancellation-label">{t('admin.cancellationReason')}:</span> {order.cancellationMessage}
                    </div>
                  )}
                  <div className="order-item-info">
                    <div className="order-restaurant">
                      <i className="fas fa-store"></i> {order.restaurantName}
                    </div>
                    {(order.user || order.customerName || order.customerEmail || order.customerPhone) && (
                      <>
                        <div className="order-customer-name">
                          <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                        </div>
{(order.customerPhone || order.user?.phoneNumber) && (
                            <div className="order-customer-phone">
                              <i className="fas fa-phone"></i> {order.customerPhone ?? order.user?.phoneNumber}
                          </div>
                        )}
                        {(order.user?.email || order.customerEmail) && (
                          <div className="order-customer-email">
                            <i className="fas fa-envelope"></i> {order.user?.email ?? order.customerEmail}
                          </div>
                        )}
                      </>
                    )}
                    <div className="order-date">
                      <i className="fas fa-clock"></i> {formatDate(order.createdAt)}
                    </div>
                    {order.deliveryAddress && (
                      <div className="order-address">
                        <i className="fas fa-map-marker-alt"></i> 
                        {order.deliveryAddress.street || order.deliveryAddress.label || 'Address not available'}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}
                      </div>
                    )}
                    <div className="order-total">
                      <strong>Total: €{order.total?.toFixed(2) || '0.00'}</strong>
                    </div>
                    {(order.driverInstructions || order.customerInstructions) && (user?.role === 'delivery_driver' || user?.role === 'main_admin' || user?.role === 'admin') && (
                      <div className="order-driver-instructions">
                        <i className="fas fa-info-circle"></i> <strong>{t('admin.driverInstructions')}:</strong> {order.driverInstructions || order.customerInstructions}
                      </div>
                    )}
                    {user?.role === 'delivery_driver' && (
                      <div className="order-chat-wrap" onClick={e => e.stopPropagation()}>
                        <Link
                          to="/admin/driver/messages"
                          state={{ orderId: order._id }}
                          className="btn-order-chat"
                        >
                          <i className="fas fa-comments"></i> {t('admin.orderChat')}
                        </Link>
                      </div>
                    )}
                    {user?.role === 'delivery_driver' && isAvailableForClaim(order) && (
                      <div className="order-accept-delivery" onClick={e => e.stopPropagation()}>
                        <p className="order-accept-delivery-label">{t('admin.acceptOrderPrompt') || 'Accept this order first. Restaurant will then confirm and prepare.'}</p>
                        <div className="order-accept-delivery-buttons">
                          <button type="button" className="btn-accept-delivery" onClick={() => handleAcceptOrder(order._id)}>
                            {t('admin.acceptOrder') || 'Accept order'}
                          </button>
                        </div>
                      </div>
                    )}
                    {user?.role === 'delivery_driver' && isPendingDriverAcceptance(order) && (
                      <div className="order-accept-delivery" onClick={e => e.stopPropagation()}>
                        <p className="order-accept-delivery-label">{t('admin.acceptDeliveryPrompt') || 'You have been assigned this delivery. Accept?'}</p>
                        <div className="order-accept-delivery-buttons">
                          <button type="button" className="btn-accept-delivery" onClick={() => handleAcceptDelivery(order._id)}>
                            {t('admin.acceptDelivery') || 'Accept delivery'}
                          </button>
                          <button type="button" className="btn-decline-delivery" onClick={() => handleDeclineDelivery(order._id)}>
                            {t('admin.declineDelivery') || 'Decline'}
                          </button>
                        </div>
                      </div>
                    )}
                    {((user?.role === 'main_admin' || user?.role === 'admin') || user?.role === 'delivery_driver') && getAvailableStatuses(order).length > 0 && !isPendingDriverAcceptance(order) && (
                      <div className="order-status-actions">
                        <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                          Change Status:
                        </label>
                        <div className="order-status-row" onClick={e => e.stopPropagation()}>
                          <select
                            value={order.status}
                            onChange={(e) => {
                              if (e.target.value !== order.status) {
                                handleStatusChange(order._id, e.target.value);
                              }
                            }}
                            className="status-select"
                          >
                            <option value={order.status}>
                              Current: {order.status}
                            </option>
                            {getAvailableStatuses(order).map(status => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                          {user?.role === 'delivery_driver' && getAvailableStatuses(order).includes('cancelled') && (
                            <button
                              type="button"
                              className="btn-cancel-order-inline"
                              onClick={() => handleStatusChange(order._id, 'cancelled')}
                            >
                              {t('admin.cancelOrder')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          {filteredOrders.length > 0 && (
            <AdminPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredOrders.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
        )}

        <div className={`map-panel ${isMobile && selectedOrder && !mobileShowList ? 'map-panel-mobile-active' : ''}`}>
          {isMobile && selectedOrder && !mobileShowList && (
            <button type="button" className="back-to-orders-btn" onClick={() => setMobileShowList(true)} aria-label={t('admin.backToOrdersList')}>
              <i className="fas fa-arrow-left"></i> {t('admin.backToOrdersList')}
            </button>
          )}
          {!googleMapsLoaded ? (
            <div className="map-loading">
              <i className="fas fa-map"></i>
              <p>{t('admin.loadingMap')}</p>
            </div>
          ) : (
            <div ref={mapRef} className="google-map"></div>
          )}
        </div>
      </div>

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={cancelOrderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => { setShowCancelModal(false); setCancelOrderId(null); setCancelOrderNumber(null); }}
      />
    </div>
  );
};

export default DeliveryView;
